import { createSelector, createSlice } from '@reduxjs/toolkit';
import { LOCATION_CHANGE } from 'connected-react-router';
import { cloneDeep } from 'lodash';
import CONSTANTS from '../../../features/analyses/Freeform/FreeformAnalysisLayout/AddReportDataLayoutContainer/Constants';

export const initialState = {
  layout: {
    showRightPanel: true,
    showRightPanel2: false,
    activeSectionIndex: 0,
    floatingTabsHeight: 0,
    floatingTabsWidth: 0,
    floatingTabsTop: 0,
  },
  libraryItem: {
    draggableId: null,
  },
  libraryItemPosition: {
    start: null,
    finish: null,
  },
  reportPreview: {
    loading: null,
    error: null,
    errorMessage: null,
    data: null,
  },
};

const reportFormatSlice = createSlice({
  name: 'reportFormat',
  initialState,
  reducers: {
    toggleRightPanel: (state) => ({
      ...state,
      layout: {
        ...state.layout,
        showRightPanel: !state.layout.showRightPanel,
      },
    }),
    toggleRightPanel2: (state) => ({
      ...state,
      layout: {
        ...state.layout,
        showRightPanel2: !state.layout.showRightPanel2,
      },
    }),
    setActiveSectionId: (state, { payload: { index } }) => ({
      ...state,
      layout: {
        ...state.layout,
        activeSectionIndex: index,
      },
    }),
    setFloatingTabsHeight: (state, { payload: { height } }) => ({
      ...state,
      layout: {
        ...state.layout,
        floatingTabsHeight: height,
      },
    }),
    setFloatingTabsWidth: (state, { payload: { width } }) => ({
      ...state,
      layout: {
        ...state.layout,
        floatingTabsWidth: width,
      },
    }),
    setFloatingTabsTop: (state, { payload: { top } }) => ({
      ...state,
      layout: {
        ...state.layout,
        floatingTabsTop: top,
      },
    }),
    setLibraryItemStartPosition: (state, action) => {
      const { start, draggableId } = action.payload;
      state.libraryItemPosition.start = start;
      state.libraryItem.draggableId = draggableId;
    },
    setLibraryItemFinishPosition: (state, action) => {
      const { finish } = action.payload;
      state.libraryItemPosition.finish = finish;
    },
    setReportPreviewData: (state, action) => {
      const { data } = action.payload;
      state.reportPreview.data = cloneDeep(data);
    },
    setReportPreviewLoading: (state, action) => {
      const { loading } = action.payload;
      state.reportPreview.loading = loading;
    },
    setReportPreviewError: (state, action) => {
      const { error, errorMessage } = action.payload;
      const { errorMessage: existingMessage } = state.reportPreview;
      state.reportPreview.error = error;
      state.reportPreview.errorMessage = errorMessage || existingMessage;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(LOCATION_CHANGE, (state, action) => {
      const {
        location: { pathname },
      } = action.payload;
      if (
        (pathname.includes(`/${CONSTANTS.PARTIAL_ROUTE.EDIT_LAYOUT_PREVIEW}`) ||
          pathname.includes(
            `/${CONSTANTS.PARTIAL_ROUTE.CREATE_LAYOUT_PREVIEW}`
          )) &&
        pathname.includes('analyses')
      ) {
        if (state.reportPreview.data) {
          state.reportPreview.data = null;
        }
      }
    });
  },
});

// Actions
export const {
  toggleRightPanel,
  toggleRightPanel2,
  setActiveSectionId,
  setFloatingTabsHeight,
  setFloatingTabsWidth,
  setFloatingTabsTop,
  setLibraryItemStartPosition,
  setLibraryItemFinishPosition,
  setReportPreviewData,
  setReportPreviewLoading,
  setReportPreviewError,
} = reportFormatSlice.actions;

// Selectors
const reportFormatState = (state) => state.reportFormat;

export const getReportFormatLayoutState = createSelector(
  reportFormatState,
  (slice) => slice.layout
);

export const getLibraryItemPosition = createSelector(
  reportFormatState,
  (slice) => slice.libraryItemPosition
);

export const getLibraryItem = createSelector(
  reportFormatState,
  (slice) => slice.libraryItem
);

export const getReportPreviewState = createSelector(
  reportFormatState,
  (slice) => slice.reportPreview
);

export default reportFormatSlice.reducer;
